import { Injectable } from '@angular/core';

interface DeltaOp {
    insert?: string;
    [key: string]: any;
}

interface DeltaFormat {
    ops: DeltaOp[];
}

@Injectable({ providedIn: 'root' })
export class LocalizedDataUtility {

    /**
     * Extracts text from Delta format JSON strings
     * @param jsonString - JSON string in Delta format or plain text
     * @returns Extracted text or original string if parsing fails
     */
    extractText(jsonString: string | undefined | null): string {
        if (!jsonString) return '';

        try {
            const parsed = this.tryParseJson(jsonString);

            if (this.isDeltaFormat(parsed)) {
                return this.extractDeltaText(parsed as DeltaFormat);
            }

            return jsonString;
        } catch (error) {
            console.error('Error parsing rich text delta:', error);
            return jsonString;
        }
    }

    /**
     * Extracts localized data with Delta format text parsing
     * @param data - Localized data object with potential Delta format fields
     * @param fieldsToExtract - Array of field names to extract (e.g., ['description', 'validityText'])
     * @returns Cleaned data with extracted text
     */
    extractLocalizedData<T>(data: T, fieldsToExtract: string[]): T {
        const cleanData = JSON.parse(JSON.stringify(data));

        fieldsToExtract.forEach(field => {
            if (cleanData[field] && typeof cleanData[field] === 'object') {
                Object.keys(cleanData[field]).forEach(locale => {
                    cleanData[field][locale] = this.extractText(cleanData[field][locale]);
                });
            }
        });

        return cleanData;
    }

    /**
     * Attempts to parse a JSON string
     * @param jsonString - String to parse
     * @returns Parsed object or null if parsing fails
     */
    private tryParseJson(jsonString: string): any {
        try {
            return JSON.parse(jsonString);
        } catch {
            return null;
        }
    }

    /**
     * Checks if an object is in Delta format
     * @param parsed - Parsed object to check
     * @returns True if object appears to be Delta format
     */
    private isDeltaFormat(parsed: any): boolean {
        return parsed && typeof parsed === 'object' && Array.isArray(parsed.ops);
    }

    /**
     * Extracts text from a parsed Delta object
     * @param delta - Parsed Delta format object
     * @returns Extracted text
     */
    private extractDeltaText(delta: DeltaFormat): string {
        return delta.ops
            .filter((op: DeltaOp) => typeof op.insert === 'string')
            .map((op: DeltaOp) => op.insert)
            .join('')
            .trim();
    }
}